import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Link } from 'react-router-dom';
import {
    MessageSquare,
    Plus,
    Search,
    AlertCircle,
    CheckCircle,
    Clock,
    HelpCircle,
    FileText
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_review: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
};

const PRIORITY_COLORS = {
    low: 'text-gray-500',
    medium: 'text-blue-500',
    high: 'text-orange-500',
    urgent: 'text-red-500',
};

export default function TicketsPage() {
    const { getAuthHeader } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'general',
        priority: 'medium',
        description: ''
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tickets`, {
                headers: getAuthHeader()
            });
            setTickets(response.data.tickets);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/tickets`, newTicket, {
                headers: getAuthHeader()
            });
            toast.success('Ticket created successfully');
            setTickets([
                {
                    id: res.data.ticket_id,
                    ...newTicket,
                    status: 'pending',
                    created_at: new Date().toISOString()
                },
                ...tickets
            ]);
            setCreateOpen(false);
            setNewTicket({ subject: '', category: 'general', priority: 'medium', description: '' });
        } catch (error) {
            toast.error('Failed to create ticket');
        } finally {
            setCreateLoading(false);
        }
    };

    const filteredTickets = tickets.filter(t =>
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        t.id.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-navy-200 rounded w-1/4" />
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="h-24 bg-navy-200 rounded-xl" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8" data-testid="tickets-page">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-navy-900 mb-1">Support Tickets</h1>
                    <p className="text-navy-600">Track and manage your support requests</p>
                </div>

                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-navy-900 hover:bg-navy-800">
                            <Plus className="w-4 h-4 mr-2" />
                            New Ticket
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Create New Ticket</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Subject</Label>
                                <Input
                                    placeholder="Brief summary of the issue"
                                    value={newTicket.subject}
                                    onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <select
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                                        value={newTicket.category}
                                        onChange={e => setNewTicket({ ...newTicket, category: e.target.value })}
                                    >
                                        <option value="general">General</option>
                                        <option value="technical">Technical Issue</option>
                                        <option value="billing">Billing</option>
                                        <option value="account">Account</option>
                                        <option value="report">Report Violation</option>
                                        <option value="feature">Feature Request</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Priority</Label>
                                    <select
                                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                                        value={newTicket.priority}
                                        onChange={e => setNewTicket({ ...newTicket, priority: e.target.value })}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea
                                    placeholder="Detailed description of your issue..."
                                    value={newTicket.description}
                                    onChange={e => setNewTicket({ ...newTicket, description: e.target.value })}
                                    className="min-h-[120px]"
                                    required
                                />
                            </div>

                            <div className="flex justify-end pt-2">
                                <Button type="submit" disabled={createLoading}>
                                    {createLoading ? 'Creating...' : 'Submit Ticket'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="bg-white rounded-xl border border-navy-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-navy-100 bg-navy-50/50 flex gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                            placeholder="Search tickets..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 bg-white"
                        />
                    </div>
                </div>

                {filteredTickets.length === 0 ? (
                    <div className="p-12 text-center">
                        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-navy-200" />
                        <h3 className="text-lg font-medium text-navy-900">No tickets found</h3>
                        <p className="text-navy-500">You haven't created any support tickets yet.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-navy-100">
                        {filteredTickets.map((ticket) => (
                            <Link
                                key={ticket.id}
                                to={`${ticket.id}`}
                                className="block p-4 hover:bg-navy-50 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="font-semibold text-navy-900">#{ticket.id.slice(0, 8)}</span>
                                            <Badge variant="secondary" className={STATUS_COLORS[ticket.status]}>
                                                {ticket.status.replace('_', ' ').toUpperCase()}
                                            </Badge>
                                            <Badge variant="outline" className="capitalize">
                                                {ticket.category}
                                            </Badge>
                                        </div>
                                        <h3 className="font-medium text-navy-800 mb-1">{ticket.subject}</h3>
                                        <p className="text-sm text-navy-500 line-clamp-1">{ticket.description}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <span className="text-xs text-navy-400 block mb-2">
                                            {formatDate(ticket.updated_at || ticket.created_at)}
                                        </span>
                                        <div className={`text-xs font-medium uppercase ${PRIORITY_COLORS[ticket.priority]}`}>
                                            {ticket.priority} Priority
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
